import { dialogMgr } from 'widgets/toolbox/dialogMgr';

const ESCAPE_CODE = 27;

/**
 * @description Base CoreModal implementation
 * @param Modal Base widget for extending
 * @returns CoreModal class
 */
function CoreModalClassCreator(Modal: import('widgets/global/Modal').TModal) {
    /**
     * @category widgets
     * @subcategory global
     * @class CoreModal
     * @augments Modal
     * @classdesc CoreModal dialog, which could be used to edit product details in different contexts, like
     * - Edit product in the cart
     * - Edit product in the wishlist
     */
    class CoreModal extends Modal {
        analyticsOnCancel: Record<string, unknown> | undefined;

        prefs() {
            return {
                disableHiding: false,
                emitEventClickOutSide: '',
                classesInitialized: 'm-initialized',
                ...super.prefs(),
                accessibilityAlerts: {
                    ...super.prefs().accessibilityAlerts,
                    dialogContentLoaded: ''
                }
            };
        }

        init() {
            super.init();

            const container = this.ref(this.prefs().refContainer);

            container.addClass(this.prefs().classesInitialized);
        }

        /**
         * @description Show Modal and puts it to the top of opened modals hierarchy.
         * @param [templateData] data to be rendered in template
         * @param [cb] optional callback
         * @param [isAsyncModalData] is asynchronous modal data
         */
        showModal(templateData?: Record<string, unknown>, cb?: () => void, isAsyncModalData?: boolean) {
            if (this.isDialogOpen) {
                this.closeModal();
            }

            this.analyticsOnCancel = templateData?.analyticsOnCancel as Record<string, unknown> | undefined;

            return super.showModal(templateData, cb, isAsyncModalData).then(() => {
                if (templateData?.analytics) {
                    this.eventBus().emit('gtm.event', templateData.analytics);
                    delete templateData.analytics;
                }
            });
        }

        /**
         * @description Cancel Handler without analytics
         * @emits Modal#cancel
         */
        cancelWithoutAnalytics() {
            this.analyticsOnCancel = undefined;
            this.cancel();
        }

        /**
         * @description Cancel Handler
         * @emits Modal#cancel
         */
        cancel() {
            super.cancel();

            if (this.analyticsOnCancel) {
                this.eventBus().emit('gtm.event', this.analyticsOnCancel);
                this.analyticsOnCancel = undefined;
            }
        }

        /**
         * @description Close Confirm Dialog
         */
        closeModal() {
            dialogMgr.closeDialog();

            if (this.backFocusElement) {
                this.backFocusElement.focus({ preventScroll: true });
                this.backFocusElement = null;
            }

            if (!this.prefs().disableRendering) {
                const refContainer = this.ref(this.prefs().refContainer);

                if (!this.prefs().disableHiding) {
                    refContainer.hide();
                }

                this.ref(this.prefs().refContent).empty();
                this.ref(this.prefs().refTitle).empty();
                this.ref(this.prefs().refDialog).attr('aria-label', refContainer.data<string>('labelLoading'));
            }

            this.onAfterCloseModal();

            if (dialogMgr.dialogsStack.length && dialogMgr.dialogsStack[0].classesGlobalDialog !== this.classesGlobalDialog) {
                this.removeGlobalDialogClass();
            }
        }

        /**
         * @description Add Click Outside / Close by ESC Listener
         * Overrides parent method to enable tracking of the modal close event when clicking outside it.
         */
        addListeners() {
            if (this.prefs().clickOutSide) {
                this.clickOutsideHandler = this.ev(
                    'click',
                    (_, event) => {
                        if (event.target === this.ref(this.prefs().refContainer).get()) {
                            this.cancel();

                            if (this.prefs().emitEventClickOutSide) {
                                this.eventBus().emit(this.prefs().emitEventClickOutSide);
                            }
                        }
                    },
                    this.ref(this.prefs().refContainer).get()
                ).pop();
            }

            if (this.prefs().closeByEscape) {
                this.escHandler = this.ev(
                    'keyup',
                    (_, event) => {
                        const keyboardEvent = <KeyboardEvent>event;

                        if (keyboardEvent.keyCode === ESCAPE_CODE) {
                            this.cancel();
                        }
                    },
                    window
                ).pop();
            }
        }
    }

    return CoreModal;
}

export type TCoreModal = ReturnType<typeof CoreModalClassCreator>;

export type TCoreModalInstance = InstanceType<TCoreModal>;

export default CoreModalClassCreator;
